import React, { useState } from "react";
import { toast } from "react-toastify";
import { downloadReportPdf, sanitizeReportFilename } from "../../utils/reportExport";
import { useWelcomeBanner } from "../../hooks/useWelcomeBanner";
import "../Header/Header.scss";
import BackArrow from "../../assets/images/back-arrow.svg";
import { useLocation } from "react-router-dom";
import { useContextValue } from "../../context/LoaderContext";
import HeaderWorkClock from "../HeaderWorkClock/HeaderWorkClock";
import { isAdminUser } from "../../utils/roles";
import { formatUserDisplayName } from "../../utils/common";

const PAGE_TITLES = [
  { match: '/main/account', title: 'Account Settings', subtitle: 'Manage your profile and subscription.' },
  { match: '/main/home', title: 'Dashboard' },
  { match: '/main/trip', title: 'Trips Overview' },
  { match: '/main/zone', title: 'Zone Activity' },
  { match: '/main/reports', title: 'Reports', subtitle: 'Create, preview, and export device activity reports.' },
  { match: '/main/report-pdf', title: 'Report Preview' },
  { match: '/main/user', title: 'User Management' },
  { match: '/main/subscription', title: 'Subscription Management' },
  { match: '/main/health', title: 'System Health' },
  { match: '/main/time-tracking', title: 'Time Tracking' },
  { match: '/main/upgrade-plan', title: 'Account Settings' },
];

const Header = () => {
  const location = useLocation()
  const { userDetails, device, reportPdfRef } = useContextValue();
  const [isExportingReport, setIsExportingReport] = useState(false);
  const adminUser = isAdminUser();
  const displayName = formatUserDisplayName(userDetails);
  const welcomeFirstName = userDetails?.firstname?.trim() || displayName || 'there';
  const showWelcomeBanner = useWelcomeBanner(Boolean(userDetails));

  const getPageTitle = () => {
    const pathname = location.pathname || '';

    if ((pathname.includes('home') || pathname.includes('trip')) && device) {
      return device;
    }

    const matchedPage = PAGE_TITLES.find(({ match }) => pathname.includes(match));
    if (matchedPage) {
      return matchedPage.title;
    }

    if (pathname.includes('home') && displayName) {
      return `Hi, ${displayName}`;
    }

    return adminUser ? 'Admin' : 'LocShark';
  };

  const getPageSubtitle = () => {
    const pathname = location.pathname || '';
    const matchedPage = PAGE_TITLES.find(({ match }) => pathname.includes(match));
    return matchedPage?.subtitle || '';
  };

  const data = [
    { name: "John", age: 30, city: "New York" },
    { name: "Jane", age: 25, city: "San Francisco" },
    { name: "Doe", age: 35, city: "Los Angeles" }
  ];

  const columnsOptions1 = [
    { header: "Device", key: 'device' },
    { header: "Status", key: 'status', },
    { header: "Location", key: 'location', },
    { header: "Command", key: 'command', },
    { header: "Battery", key: 'battery', },
    { header: "Date / Time", key: 'date' }
  ]
  const rowData1 = [
    {
      device: "LocShark Tablet #1",
      status: 'Moving',
      location: 'Alsfeld Ranch, Braunridge, San Antonio, Bexar County, Texas, United States',
      command: 'Clamp',
      battery: '56%',
      date: '25 Apr 2024 18:01:38'
    },
    {
      device: "LocShark Tablet #2",
      status: 'Parked',
      location: 'Alsfeld Ranch, Braunridge, San Antonio, Bexar County, Texas, United States',
      command: 'Clamp',
      battery: '56%',
      date: '25 Apr 2024 18:01:38'
    },
    {
      device: "LocShark Tablet #3",
      status: 'Moving',
      location: 'Alsfeld Ranch, Braunridge, San Antonio, Bexar County, Texas, United States',
      command: 'Clamp',
      battery: '56%',
      date: '25 Apr 2024 18:01:38'
    },
    {
      device: "LocShark Tablet #4",
      status: 'Idle',
      location: 'Alsfeld Ranch, Braunridge, San Antonio, Bexar County, Texas, United States',
      command: 'Clamp',
      battery: '56%',
      date: '25 Apr 2024 18:01:38'
    },
    {
      device: "LocShark Tablet #5",
      status: 'Offline',
      location: 'Alsfeld Ranch, Braunridge, San Antonio, Bexar County, Texas, United States',
      command: 'Clamp',
      battery: '56%',
      date: '25 Apr 2024 18:01:38'
    },
  ];

  // Function to convert the data to CSV and download it
  const exportCSV = () => {
    const csvRows = [];
    const headers = columnsOptions1.map(col => col.key);
    // const headers = Object.keys(columnsOptions1);
    csvRows.push(headers.join(','));

    // Add data rows
    for (const row of rowData1) {
      const values = headers.map(header => row[header]);
      // csvRows.push(values);
      csvRows.push(values.join(','));
    }

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'data.csv';
    link.click();
  };

  const pageSubtitle = getPageSubtitle();
  const isReportsPage = location?.pathname?.includes('reports')
    && !location?.pathname?.includes('report-pdf');

  const handleReportsExport = async () => {
    const element = reportPdfRef?.current;
    if (!element) {
      toast.error('Select a report to export.');
      return;
    }

    const titleEl = element.querySelector('.reports-report-name');
    const filename = `${sanitizeReportFilename(titleEl?.textContent || 'locshark-report')}.pdf`;

    setIsExportingReport(true);
    try {
      await downloadReportPdf(element, filename);
      toast.success('Report downloaded.');
    } catch (error) {
      console.error('Error exporting report PDF:', error);
      toast.error('Unable to download report. Please try again.');
    } finally {
      setIsExportingReport(false);
    }
  };

  return (
    <>
      <header
        className={`header${showWelcomeBanner ? ' header--welcome' : ''}${pageSubtitle ? ' header--has-subtitle' : ''}`}
      >
        {showWelcomeBanner && (
          <div className="header-welcome-banner" role="status" aria-live="polite">
            <span className="header-welcome-banner-text">
              Welcome back, {welcomeFirstName}!
            </span>
            <span className="header-welcome-banner-sub">
              You&apos;re signed in to LocShark.
            </span>
          </div>
        )}
        <div className="header_inner">
          <div className="left_part">
            <button
              className="btn btn-light btn_close d-lg-none d-md-block"
              type="button"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvasExample"
              aria-controls="offcanvasExample"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 448 512"
              >
                <path d="M0 96C0 78.3 14.3 64 32 64H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 128 0 113.7 0 96zM0 256c0-17.7 14.3-32 32-32H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32c-17.7 0-32-14.3-32-32zM448 416c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32s14.3-32 32-32H416c17.7 0 32 14.3 32 32z" />
              </svg>
            </button>
            <div className="page-title-block">
              <div className="page-title-copy">
                <h3>
                  {location.pathname.includes('detail') && (
                    <img src={BackArrow} alt="BackArrow" onClick={() => window.history.back()} className="back-arrow" />
                  )}
                  {getPageTitle()}
                </h3>
                {pageSubtitle && (
                  <p className="page-title-subtitle">{pageSubtitle}</p>
                )}
              </div>
            </div>
          </div>
          <div className="right_part header-right-tools">
            {!adminUser && <HeaderWorkClock />}
            {isReportsPage && (
              <button
                type="button"
                className="reports-export-btn"
                onClick={handleReportsExport}
                disabled={isExportingReport}
                aria-busy={isExportingReport}
              >
                <i className="bi bi-file-earmark-pdf" aria-hidden="true" />
                {isExportingReport ? 'Exporting…' : 'Export PDF'}
              </button>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
